import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler, successHandler } from "../../../helpers/handlers";

const initialState = {
  slots: {},
  loading: false,
  error: null,
  actionLoading: false,
};

const slice = createSlice({
  name: "calendar",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSlots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.slots = action.payload;
        state.error = null;
      })
      .addCase(fetchSlots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createFreeSlots.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createFreeSlots.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { date, slots } = action.payload;
        state.slots[date] = slots;
        successHandler("Free slots created successfully!");
      })
      .addCase(createFreeSlots.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteSlots.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteSlots.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { slotIds } = action.payload;

        // Create a new object for state.slots to ensure immutability
        const updatedSlots = Object.keys(state.slots).reduce((acc, dateKey) => {
          const filteredSlots = state.slots[dateKey].filter(
            (slot) => !slotIds.includes(slot.id)
          );

          // Only include dates that still have slots
          if (filteredSlots.length > 0) {
            acc[dateKey] = filteredSlots;
          }

          return acc;
        }, {});

        state.slots = updatedSlots; // Replace the slots with the updated object

        successHandler("Slots deleted successfully!");
      })
      .addCase(deleteSlots.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      });
  },
});

// Async thunk to fetch slots
export const fetchSlots = createAsyncThunk(
  "calendar/fetchSlots",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/consultations");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const createFreeSlots = createAsyncThunk(
  "calendar/createFreeSlots",
  async (
    { date, startTime, endTime, duration = 30, breakTime = 30 },
    { rejectWithValue }
  ) => {
    try {
      // Check if startTime is missing and set a default value
      if (!startTime) {
        const defaultStartTime = "09:00";
        const startDateTime = new Date(`${date}T${defaultStartTime}:00`);
        startTime = startDateTime.toISOString().split("T")[1].slice(0, 5); // Extract UTC time (HH:mm)
      }

      // Check if endTime is missing and set a default value
      if (!endTime) {
        const defaultEndTime = "17:00";
        const endDateTime = new Date(`${date}T${defaultEndTime}:00`);
        endTime = endDateTime.toISOString().split("T")[1].slice(0, 5); // Extract UTC time (HH:mm)
      }

      console.log("Creating free slots with params:", {
        date,
        startTime,
        endTime,
        duration,
        breakTime,
      });

      const response = await api.post("/admin/consultations", {
        date,
        startTime,
        endTime,
        duration,
        breakTime,
      });
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSlots = createAsyncThunk(
  "calendar/deleteSlots",
  async (slotIds, { rejectWithValue }) => {
    try {
      await api.delete("/admin/consultations", {
        data: { slotIds }, // Pass slotIds in the request body
      });
      return { slotIds };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export default slice.reducer;
